/**
 * BackupCodeEntry - Backup code entry form for 2FA recovery
 *
 * Used when user loses access to their authenticator app.
 * Accepts backup codes in XXXX-XXXX format.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { KeyRound, Loader2, ArrowLeft, AlertTriangle } from 'lucide-react';

export interface BackupCodeEntryProps {
  /** Callback to verify the backup code */
  onVerify: (code: string) => Promise<boolean>;
  /** Callback to go back to TOTP entry */
  onBack: () => void;
  /** Whether verification is in progress */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
}

export function BackupCodeEntry({
  onVerify,
  onBack,
  loading = false,
  error,
}: BackupCodeEntryProps) {
  const [backupCode, setBackupCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const formatBackupCode = (value: string): string => {
    // Remove non-alphanumeric characters
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    // Insert hyphen after 4 characters
    if (cleaned.length > 4) {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}`;
    }
    return cleaned;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatBackupCode(e.target.value);
    if (formatted.length <= 9) { // XXXX-XXXX = 9 chars
      setBackupCode(formatted);
      setLocalError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate format
    const codeWithoutHyphen = backupCode.replace('-', '');
    if (codeWithoutHyphen.length !== 8) {
      setLocalError('Please enter a valid 8-character backup code');
      return;
    }

    const success = await onVerify(backupCode);
    if (!success) {
      setBackupCode('');
    }
  };

  const displayError = error || localError;

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1 text-center">
        <div className="flex justify-center mb-2">
          <div className="p-3 rounded-full bg-amber-100 dark:bg-amber-900/30">
            <KeyRound className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
        </div>
        <CardTitle className="text-2xl">Use a Backup Code</CardTitle>
        <CardDescription>
          Enter one of the backup codes you saved when setting up 2FA
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Alert */}
          {displayError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{displayError}</AlertDescription>
            </Alert>
          )}

          {/* Backup Code Input */}
          <div className="space-y-2">
            <Label htmlFor="backup-code">Backup Code</Label>
            <Input
              id="backup-code"
              type="text"
              value={backupCode}
              onChange={handleInputChange}
              placeholder="XXXX-XXXX"
              className="text-center font-mono text-lg tracking-wider"
              disabled={loading}
              autoFocus
              autoComplete="one-time-code"
            />
            <p className="text-xs text-muted-foreground text-center">
              Each backup code can only be used once
            </p>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            disabled={backupCode.replace('-', '').length !== 8 || loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify Backup Code'
            )}
          </Button>

          {/* Back to TOTP */}
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={onBack}
            disabled={loading}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to authenticator code
          </Button>

          {/* Help Text */}
          <div className="text-center text-sm text-muted-foreground">
            <p>
              Don't have your backup codes?{' '}
              <a
                href="mailto:support@calie.ai"
                className="text-primary hover:underline"
              >
                Contact support
              </a>
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
